import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";

import { getLoanView, type DashboardResponse } from "../api/loanOperationsApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { DateValue, FinancialAmount, RatePercentage } from "../components/FinancialValue";
import { MetricCard } from "../components/MetricCard";

export function DashboardPage({ onNavigate }: { onNavigate(path: string): void }) {
  const { api } = useContext(EmbeddedSessionContext);
  const [state, setState] = useState<{ status: "loading" | "error" } | { status: "ready"; value: DashboardResponse }>({ status: "loading" });

  useEffect(() => {
    if (!api) return;
    let active = true;
    void getLoanView<DashboardResponse>(api, "dashboard")
      .then((value) => active && setState({ status: "ready", value }))
      .catch(() => active && setState({ status: "error" }));
    return () => { active = false; };
  }, [api]);

  if (state.status === "loading") return <Loader loaderLabel="Loading portfolio" size="medium" />;
  if (state.status === "error") return <Message open title="Portfolio could not be loaded" variant="error">Try reopening Loan Management.</Message>;
  if (state.status !== "ready") return null;

  const { summary } = state.value;
  return (
    <div className="operational-stack">
      <section aria-label="Portfolio summary" className="metric-grid">
        <MetricCard icon="card_wallet" label="Current principal" primary supportingText="Total outstanding principal"><FinancialAmount currency="USD" value={summary.current_principal} /></MetricCard>
        <MetricCard icon="calendar" label="Debt service · 12 months"><FinancialAmount currency="USD" value={summary.debt_service_due} /></MetricCard>
        <MetricCard icon="percentage_boxed" label="Interest · 12 months"><FinancialAmount currency="USD" value={summary.interest_due} /></MetricCard>
        <MetricCard icon="chart_line" label="Weighted average rate"><RatePercentage value={summary.weighted_rate} /></MetricCard>
      </section>

      <section aria-label="Portfolio health" className="portfolio-health">
        <MetricCard icon="calendar_pay_date" label="Balloons · 12 months"><FinancialAmount currency="USD" value={summary.balloon_due} /></MetricCard>
        <MetricCard icon="bank" label="Active loans">{String(summary.active_loans)}</MetricCard>
      </section>

      <section className="operational-section" aria-labelledby="upcoming-maturities">
        <div className="section-heading-row">
          <div><h2 id="upcoming-maturities">Upcoming maturities</h2><p>Active loan terms ordered by maturity.</p></div>
          <Button onClick={() => onNavigate("/loans")} variant="default" variantType="tertiary">View loans</Button>
        </div>
        {state.value.maturities.length === 0 ? <p className="muted-text">No active loan maturities are available.</p> : (
          <div className="maturity-list">
            {state.value.maturities.map((loan) => (
              <button className="maturity-row" key={loan.id} onClick={() => onNavigate(`/loans/${loan.id}`)} type="button">
                <span><strong>{loan.name}</strong><small>{loan.loan_number}</small></span>
                <DateValue value={loan.maturity_date} />
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}